import type {Component, PropertyProps, StyleProps} from 'grapesjs';
import {colorToHex, getFormattedHexColor, isCorrectHexFormat} from '@Utils/color.util';
import {capitalizeFirstLetter} from '@Utils/string.util';
import {noop} from '@Utils/general.util';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

export interface EmailEditorColorPickerProps extends PropertyProps {
  propertyForClass?: string;
  classToApply?: string;
  swatchesHeading: string;
  component?: Component;
  isDeletable?: boolean;
  onColorChange?: (color: string) => void;
}

export interface ColorPickerOpts {
  initialColors: string[];
  initialType: number;
  enableGradient: boolean;
  beforeColorPicking?: VoidFunction;
  showPickColorFromPage: boolean;
  showGeneralColorPickerFromPage: boolean;
  swatchesHeading: string;
  swatchColors: string[];
  inputColorSwatch: boolean;
  clearHexInput: boolean;
  showBrandColors: boolean;
  onAddBrandAsset: (cb: () => Promise<void>) => Promise<void>;

  onColorChanging?: (color: string | null) => void;
  onColorChanged?: (color: string | null, prevColor: string | null) => void;
  afterColorPicked?: (color: string | null) => void;
}

export const DEFAULT_COLOR = 'FFFFFF';

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  styleManager.addType(window.PMW.emails.styles.ID_COLOR_PICKER, {
    create(data): HTMLDivElement {
      const {change} = data;
      const props = data.props as EmailEditorColorPickerProps;

      updatePropsComponent(props);

      props.isDeletable = Object.prototype.hasOwnProperty.call(props, 'isDeletable') ? props.isDeletable : true;
      const [labelContainer, colorPickerContainer] = getColorPickerContainers(props);
      attachColorPickerHandlers(props, change, labelContainer, colorPickerContainer);

      return labelContainer;
    },

    emit(data, {labelContainer, colorPickerContainer, color}) {
      const newVal = color ? `#${color}` : 'transparent';
      const {change} = data;
      const props = data.props as EmailEditorColorPickerProps;

      updatePropsComponent(props);
      if (props.onColorChange) {
        props.onColorChange(`${newVal}`);
      }

      if (
        typeof window.PMW.formmaker !== 'undefined' &&
        props.component?.is(window.PMW.formmaker.components.structural.PMW_FORM_BACKGROUND) &&
        props.component?.parent()?.is(window.PMW.formmaker.components.structural.PMW_SIGNUP_FORM)
      ) {
        editor.runCommand(window.PMW.emails.commands.FIX_BUTTON_CONTAINER_BACKGROUND, {backgroundColor: newVal});
      }

      if (props.classToApply && props.propertyForClass) {
        const target = getElementFromComponentByClass(props.component, props.classToApply);
        if (target && target.length > 0) {
          $(target).css(props.propertyForClass, newVal);
        }
      }

      addBgColorToTable(props, newVal);

      if (props.component && isUpdatingTextColor(props.component, props?.property)) {
        editor.runCommand(window.PMW.emails.commands.SET_TEXT_COLOR_STYLE, {
          component: props.component,
          property: props.property,
          value: newVal,
        });
      } else {
        editor.runCommand(window.PMW.emails.commands.SET_COMPONENT_STYLE, {
          component: props.component,
          property: props.property,
          value: newVal,
        });
      }

      updateLabelStylesOnColorChange(props, labelContainer as HTMLElement, color as string | null);
      renderColorPicker(props, labelContainer as HTMLElement, colorPickerContainer as HTMLElement, change);
    },
  });


  function getElementFromComponentByClass(component: Component | undefined, classToSearch: string | null): JQuery<HTMLElement> | null {
    if (!component) {
      return null;
    }

    const componentElement = component.getEl();
    if (!componentElement || !classToSearch) {
      return null;
    }
    const targetElement = $(componentElement).find(`.${classToSearch}`);

    return targetElement || null;
  }

  /**
   * show/hide delete button and stop icon on label
   */
  function updateLabelStylesOnColorChange(props: EmailEditorColorPickerProps, labelContainer: HTMLElement, color: string | null = null): void {
    const containerEl = $(labelContainer);
    const deleteBtn = containerEl.find('.js-colorpicker-delete-btn');
    const labelBtn = containerEl.find('.js-colorpicker-label-btn');
    const addIcon = labelBtn.find('.js-colorpicker-label-icon');
    const addText = labelBtn.find('.js-colorpicker-label-text');

    labelBtn.css('background', `#${color || DEFAULT_COLOR}`);
    if (color) {
      labelBtn.removeClass('-empty');
      addIcon.addClass('_hidden');
      addText.addClass('_hidden');

      if (props.isDeletable) {
        deleteBtn.removeClass('_hidden');
      }
    } else {
      labelBtn.addClass('-empty');
      deleteBtn.addClass('_hidden');
      addIcon.removeClass('_hidden');
      addText.removeClass('_hidden');
    }
  }

  function getColorPickerContainers(props: EmailEditorColorPickerProps): HTMLDivElement[] {
    const labelContainer = document.createElement('div');
    const colorPickerContainer = document.createElement('div');
    const initialColor = getFormattedInitialColor(props);

    if (typeof props.type !== 'undefined') {
      labelContainer.className = props.type;
      colorPickerContainer.className = props.type;
    }

    labelContainer.innerHTML = `<div class="label-container">
                <span class="style-manager-label">${props.label}</span>
                <div class="btn-container">
                    <button class="btn clear-btn js-colorpicker-delete-btn ${initialColor && props.isDeletable ? '' : '_hidden'}" aria-label="${window.i18next.t('pmwjs_remove_color')}">
                        <i class="icon icon-delete body-m"></i>
                    </button> 
                    <button class="btn color-fill-display js-colorpicker-label-btn ${initialColor ? '' : '-empty'}" style="background: #${initialColor ?? DEFAULT_COLOR};"
                        title="${window.i18next.t('pmwjs_pick_a_color')}">
                        <i class="icon icon-plus js-colorpicker-label-icon spacing-m-0 content-body ${initialColor ? '_hidden' : ''}"></i>
                    </button>
                </div>
            </div>`;

    return [labelContainer, colorPickerContainer];
  }

  /**
   * @param {object} props
   * @param {function} change function to trigger component's emit
   * @param {HTMLElement} labelContainer
   * @param {HTMLElement} colorPickerContainer
   */
  function attachColorPickerHandlers(props: EmailEditorColorPickerProps, change: (...args: any) => void, labelContainer: HTMLElement, colorPickerContainer: HTMLElement): void {
    window.PMW.onClick($(labelContainer), '.js-colorpicker-label-btn', () => {
      renderColorPicker(props, labelContainer, colorPickerContainer, change);
      editor.runCommand(window.PMW.emails.commands.OPEN_SIDE_PANEL, {
        title: props.label,
        content: colorPickerContainer,
      });
    });

    window.PMW.onClick($(labelContainer), '.js-colorpicker-delete-btn', () => {
      change({labelContainer, colorPickerContainer});
    });
  }

  function updatePropsComponent(props: EmailEditorColorPickerProps): void {
    if (!props.component && typeof editor.getSelected() === 'undefined') {
      throw new Error('Error: No component has been selected!');
    }

    if (!props.component) {
      props.component = editor.getSelected() as Component;
    }
  }

  function renderColorPicker(props: EmailEditorColorPickerProps, labelContainer: HTMLElement, colorPickerContainer: HTMLElement, change: (...args: any) => void): void {
    function onColorChanged(color: string | null, prevColor: string | null = null): void {
      // need to revert back so UndoManager detects the change later
      editor.getModel().skip((): void => {
        if (props.property && props.component) {
          props.component.addStyle({[props.property]: `#${prevColor}`});
        }
      });
      change({labelContainer, colorPickerContainer, color}); // function to trigger component's emit
    }

    function onColorChanging(color: string | null): void {
      editor.getModel().skip(() => {
        if (typeof props.onColorChange === 'function') {
          props.onColorChange(`#${color}`);
        }

        const selected = props.component;
        if (props.property && selected) {
          const style: {[key: string]: string} = {};
          style[props.property] = `#${color}`;
          selected.addStyle(style);
        }
        change({labelContainer, colorPickerContainer, color});
      });
    }

    updatePropsComponent(props);

    const opts = getColorPickerOpts(props);
    opts.onColorChanging = onColorChanging;
    opts.showPickColorFromPage = false;
    opts.showGeneralColorPickerFromPage = true;
    opts.onColorChanged = onColorChanged;
    opts.afterColorPicked = (color): void => {
      onColorChanged(color);
      $('#colorpicker-canvas').addClass('_hidden');
    };

    window.PMW.renderColorPicker(colorPickerContainer, opts);
  }

  /**
   * @param {object} props
   * @return {object}
   */
  function getColorPickerOpts(props: EmailEditorColorPickerProps): ColorPickerOpts {
    const solidType = 0;
    const allColorsOnEmail = editor.runCommand(window.PMW.emails.commands.GET_ALL_COLORS_HEXES_IN_COMPONENT, {component: editor.getWrapper()}) as string[];
    const swatches = [...new Set(allColorsOnEmail)].sort();

    return {
      showGeneralColorPickerFromPage: false,
      initialColors: [getFormattedInitialColor(props) || DEFAULT_COLOR],
      beforeColorPicking: noop,
      afterColorPicked: noop,
      initialType: solidType,
      enableGradient: false,
      showPickColorFromPage: false,
      swatchesHeading: capitalizeFirstLetter(props.swatchesHeading),
      swatchColors: swatches,
      inputColorSwatch: false,
      clearHexInput: true,
      showBrandColors: true,
      onAddBrandAsset(cb: () => Promise<void>): Promise<void> {
        return editor.runCommand(window.PMW.emails.commands.ON_ADD_BRAND_ASSET, {cb}) as Promise<void>;
      },
    };
  }

  function getInitialColor(props: EmailEditorColorPickerProps): string {
    if (props.component && props.property) {
      const style = props.component.get('style') as StyleProps;
      return style[props.property] as string;
    }

    return props.default || 'transparent';
  }

  function getFormattedInitialColor(props: EmailEditorColorPickerProps): string | undefined {
    const color = getInitialColor(props);

    if (color === undefined || color === 'transparent') {
      return undefined;
    }

    if (isCorrectHexFormat(color)) {
      return color;
    }

    return getFormattedHexColor(colorToHex(color));
  }

  function addBgColorToTable(props: EmailEditorColorPickerProps, colorHex: string): void {
    if (
      editor.runCommand(window.PMW.emails.commands.IS_OF_TYPE_TABLE, {
        component: props.component,
      })
    ) {
      props.component?.addAttributes({
        bgcolor: colorHex,
      });
    }
  }

  function isTextBasedComponent(component: Component): boolean {
    return (
      component.get('type') === window.PMW.emails.components.text.PMW_TEXT ||
      component.get('type') === window.PMW.emails.components.default.TEXT ||
      component.get('tagName')?.toLowerCase() === 'a' ||
      component.get('type') == window.PMW.emails.components.text.PMW_BUTTON
    );
  }

  function isUpdatingTextColor(component: Component, property: string | undefined): boolean {
    return isTextBasedComponent(component) && property === 'color';
  }
};
